import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import CircularTable from './CircularTable';
import { Breadcrumb, Tabs, Tab, Dropdown, Modal, Button, Form } from "react-bootstrap";
import { Columns, Download, Filter, PencilSquare, ArrowBarRight, ChevronDown, ChevronLeft, ChevronRight, FileX, ArrowsFullscreen, ArrowClockwise } from "react-bootstrap-icons";
import { AuthService, CustomerService, LabelService, SeatingService, TransRoutesService, ReportService, trackActionLog } from '../../services';
import { PERSONAL_ROUTE_STATUS, isCustomerListActive } from '../../shared/constants';
import { formatSeatDisplayLikeSeatingChart, getSeatAssignedCustomers, pickDisplayedSeatCustomer } from '../../shared/seatingTableDisplay';
import moment from 'moment';
import Select from 'react-select';

/** Column-major table ids: col1 tables, then col2, … — matches layout rebuild order. */
function buildSeatingSlotOrderFromCounts(counts) {
	const ids = [];
	let n = 1;
	for (let c = 0; c < counts.length; c++) {
		const cnt = Math.max(0, parseInt(String(counts[c] ?? 0), 10) || 0);
		for (let t = 0; t < cnt; t++) {
			ids.push(`table-${n++}`);
		}
	}
	return ids;
}

const SEATING_PAGE_AUTO_REFRESH_MS = 60_000;

const Seating = () => {
	const canManageSeatingChart = AuthService.canEditSeatingChart();
	// OLD ROW-BASED INITIAL VALUE - COMMENTED OUT
	// const initialValue = {
	// 	rows: [{
	// 		id: 1,
	// 		tables: 1
	// 	}],
	// 	tables: {
	// 		'table-1': {
	// 			id: 1,
	// 			tableId: 'table-1',
	// 			seats: [
	// 				{ id: '1-A', name: 'A', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-B', name: 'B', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-C', name: 'C', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-D', name: 'D', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-E', name: 'E', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-F', name: 'F', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-G', name: 'G', customerId: '', customerName: '', label: {}, rowIndex: 1 },
	// 				{ id: '1-H', name: 'H', customerId: '', customerName: '', label: {}, rowIndex: 1 }
	// 			],
	// 			expanded: false,
	// 			rowIndex: 1
	// 		}
	// 	}
	// };

	// NEW COLUMN-BASED INITIAL VALUE
	const initialValue = {
		columns: [{
			id: 1,
			tables: 1
		}],
		tables: {
			'table-1': {
				id: 1,
				tableId: 'table-1',
				seats: [
					{ id: '1A', name: 'A', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1B', name: 'B', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1C', name: 'C', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1D', name: 'D', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1E', name: 'E', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1F', name: 'F', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1G', name: 'G', customerId: '', customerName: '', label: {}, columnIndex: 1 },
					{ id: '1H', name: 'H', customerId: '', customerName: '', label: {}, columnIndex: 1 }
				],
				expanded: false,
				columnIndex: 1
			}
		}
	};
	// OLD ROW-BASED STATE - COMMENTED OUT
	// const [tableLayout, setTableLayout] = useState({
	// 	rows: [],
	// 	tables: {}
	// });

	// NEW COLUMN-BASED STATE
	const [tableLayout, setTableLayout] = useState({
		columns: [],
		tables: {}
	});

	// OLD ROW-BASED LOGIC - COMMENTED OUT
	// const [rowCount, setRowCount] = useState(1);
	// const [tableCountsPerRow, setTableCountsPerRow] = useState([1]);

	// NEW COLUMN-BASED LOGIC
	const [columnCount, setColumnCount] = useState(1);
	const [tableCountsPerColumn, setTableCountsPerColumn] = useState([1]);

	const [currentDateRecord, setCurrentDateRecord] = useState(null);

	const [editingSeat, setEditingSeat] = useState(null);

	const [showTableLayoutConfig, setShowTableLayoutConfig] = useState(false);

	const [currentLabels, setCurrentLabels] = useState([]);

	const [newLabel, setNewLabel] = useState({ label_name: undefined, label_color: undefined});

	const [customers, setCustomers] = useState([]);

	const [showLabelConfig, setShowLabelConfig] = useState(false);
	
	const [originalLabelsList, setOriginalLabelsList] = useState([]);

	const [startAddLabel, setStartAddLabel] = useState(false);
	const [deletedItems, setDeletedItems] = useState([]);
	const [editingLabelId, setEditingLabelId] = useState(null);
	const [editingLabelName, setEditingLabelName] = useState('');
	const [editingLabelColorId, setEditingLabelColorId] = useState(null);
	const [editingTableId, setEditingTableId] = useState(null);
	const [editingTableNumber, setEditingTableNumber] = useState('');

	const [selectedCustomer, setSelectedCustomer] = useState([]);
	const [selectedCustomerLabel, setSelectedCustomerLabel] = useState(undefined);
	const [selectedLabelColor, setSelectedLabelColor] = useState(undefined);
	const [inCenterCustomerIds, setInCenterCustomerIds] = useState([]);
	const [notAbsentCustomerIds, setNotAbsentCustomerIds] = useState([]);
	const [isFullScreen, setIsFullScreen] = useState(false);
	/** When true, manage panel is a narrow rail; chart area grows. */
	const [manageSeatingPanelCollapsed, setManageSeatingPanelCollapsed] = useState(false);

	/** Bumped on manual refresh so the 60s auto-refresh interval resets without changing `selectedDate`. */
	const [autoRefreshScheduleToken, setAutoRefreshScheduleToken] = useState(0);

	/** Fullscreen: px gap between .seating-column; computed so the row fits clientWidth. */
	const fullscreenTablesRowRef = useRef(null);
	const [fullscreenInterColumnGapPx, setFullscreenInterColumnGapPx] = useState(null);

	const updateFullscreenTableGap = useCallback(() => {
		const el = fullscreenTablesRowRef.current;
		if (!el) return;
		const cols = el.querySelectorAll(':scope > .seating-column');
		const n = cols.length;
		if (n < 1) {
			return;
		}
		const w = el.clientWidth;
		if (w < 1) return;
		let colW = 0;
		cols.forEach((c) => {
			const cw = c.getBoundingClientRect().width;
			if (cw > colW) colW = cw;
		});
		if (colW < 40) {
			colW = 160; /* .circular-table-container width + padding before first layout measure */
		}
		if (n === 1) {
			setFullscreenInterColumnGapPx(0);
			return;
		}
		const minGap = 0;
		const maxGap = 80;
		const free = w - n * colW;
		const raw = free / (n - 1);
		const g = Math.max(minGap, Math.min(maxGap, Math.floor(raw)));
		setFullscreenInterColumnGapPx(g);
	}, []);

	useLayoutEffect(() => {
		if (!isFullScreen) {
			setFullscreenInterColumnGapPx(null);
			return;
		}
		const el = fullscreenTablesRowRef.current;
		if (!el) return;
		updateFullscreenTableGap();
		const ro = new ResizeObserver(() => {
			updateFullscreenTableGap();
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [isFullScreen, tableLayout, updateFullscreenTableGap]);

	// Meal confirmation state
	const [showMealModal, setShowMealModal] = useState(false);
	const [selectedSeatCustomer, setSelectedSeatCustomer] = useState(null);
	const [breakfastRecords, setBreakfastRecords] = useState([]);
	const [lunchRecords, setLunchRecords] = useState([]);
	const [snackRecords, setSnackRecords] = useState([]);

	const [selectedDate, setSelectedDate] = useState(new Date());

	const colorsList = ['#AD967A', '#BD816E', '#2B76E5', '#66CCFF', '#0A7E22', '#00C875', '#9CD326', '#FFCB00', '#FF642E', '#FD314D', '#BB3354', '#FF158A', '#9B51E0', '#BDA8F9'];

	const formatSeatingDisplayName = useCallback((customer) => {
		const preferredName = `${customer?.name_cn || ''}`.trim();
		if (preferredName) return preferredName;
		const firstName = `${customer?.firstname || ''}`.trim();
		const lastName = `${customer?.lastname || ''}`.trim();
		const lastInitial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : '';
		const shortName = [firstName, lastInitial].filter(Boolean).join(' ').trim();
		return shortName || `${customer?.name || ''}`.trim();
	}, []);

	const seatingDisplayNameByCustomerId = useMemo(() => {
		const displayNameMap = {};
		(customers || []).forEach((customer) => {
			if (!customer?.id) return;
			displayNameMap[customer.id] = formatSeatingDisplayName(customer);
		});
		return displayNameMap;
	}, [customers, formatSeatingDisplayName]);

	const isToday = moment(selectedDate).isSame(moment(), 'day');

	const seatingChartDateText = moment(selectedDate).format('MM/DD/YYYY');

	const exportSeatingChartDietary = async () => {
		trackActionLog({
			actionVerb: "Export",
			summary: "Tap export button (seating chart dietary)",
			entityType: "export",
			entityId: "seating-chart-dietary",
		}).catch(() => {});
		try {
			const response = await ReportService.exportSeatingChartDietaryExcel(seatingChartDateText);
			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
			});
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.setAttribute(
				"download",
				`Seating_Chart_Dietary_${seatingChartDateText.replace(/\//g, "-")}.xlsx`
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			console.error("Failed to export seating chart dietary:", error);
			let message = "Failed to export seating chart dietary records.";
			try {
				const data = error?.response?.data;
				if (data instanceof Blob) {
					const text = await data.text();
					try {
						const parsed = JSON.parse(text);
						message = parsed.message || text;
					} catch {
						message = text || message;
					}
				} else if (error?.message) {
					message = error.message;
				}
			} catch {
				/* keep default */
			}
			window.alert(message);
		}
	};

	const goToPreviousDay = () => {
		setSelectedDate(prev => {
			const d = new Date(prev);
			d.setDate(d.getDate() - 1);
			return d;
		});
	};

	const goToNextDay = () => {
		if (isToday) return;
		setSelectedDate(prev => {
			const d = new Date(prev);
			d.setDate(d.getDate() + 1);
			// Don't go beyond today
			const today = new Date();
			today.setHours(0,0,0,0);
			d.setHours(0,0,0,0);
			if (d > today) return prev;
			return d;
		});
	};

	const goToToday = () => {
		setSelectedDate(new Date());
	};

	// Fetch meal records for selected date
	const fetchMealRecords = useCallback(() => {
		const dateStr = moment(selectedDate).format('MM/DD/YYYY');
		TransRoutesService.getAllBreakfastRecords(dateStr).then(data => setBreakfastRecords(data?.data || []));
		TransRoutesService.getAllLunchRecords(dateStr).then(data => setLunchRecords(data?.data || []));
		TransRoutesService.getAllSnackRecords(dateStr).then(data => setSnackRecords(data?.data || []));
	}, [selectedDate]);

	// Fetch seating + routes data for selected date
	const fetchSeatingData = useCallback(() => {
		const dateStr = moment(selectedDate).format('MM/DD/YYYY');

		SeatingService.getAll(dateStr).then(data => {
			const result = data?.data;
			if (result?.length > 0) {
				setTableLayout(result[0]?.seating_assignment);
				setCurrentDateRecord(result[0]);
				setColumnCount(result[0]?.seating_assignment?.columns?.length || 1);
				setTableCountsPerColumn(result[0]?.seating_assignment?.columns?.map(item => item.tables) || [1]);
			} else {
				// No record for this date - fetch all records to find the most recent one on or before this date
				SeatingService.getAll().then(allData => {
					const allRecords = allData?.data || [];
					if (allRecords.length > 0) {
						const selectedTime = new Date(selectedDate);
						selectedTime.setHours(23,59,59,999);
						const sorted = allRecords
							.filter(r => {
								const parts = r?.date?.split('/');
								if (!parts || parts.length !== 3) return false;
								return new Date(parts[2], parts[0] - 1, parts[1]).getTime() <= selectedTime.getTime();
							})
							.sort((a, b) => {
								const parseDate = (d) => {
									const parts = d?.date?.split('/');
									if (!parts || parts.length !== 3) return 0;
									return new Date(parts[2], parts[0] - 1, parts[1]).getTime();
								};
								return parseDate(b) - parseDate(a);
							});
						const latest = sorted[0];
						const layout = latest?.seating_assignment;
						if (layout) {
							setTableLayout(layout);
							setColumnCount(layout?.columns?.length || 1);
							setTableCountsPerColumn(layout?.columns?.map(item => item.tables) || [1]);
							// Only auto-create a record if viewing today
							if (moment(selectedDate).isSame(moment(), 'day')) {
								SeatingService.createNewSeating({
									date: dateStr,
									seating_assignment: layout,
									create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
									update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
									create_date: new Date(),
									update_date: new Date()
								}).then((newData) => {
									setCurrentDateRecord(newData?.data);
								});
							} else {
								setCurrentDateRecord(null);
							}
						} else {
							setTableLayout(initialValue);
							setCurrentDateRecord(null);
							setColumnCount(1);
							setTableCountsPerColumn([1]);
						}
					} else {
						setTableLayout(initialValue);
						setCurrentDateRecord(null);
						setColumnCount(1);
						setTableCountsPerColumn([1]);
					}
				});
			}
		});

		// Fetch inbound routes for selected date — full-opacity seat for picked or in-center
		TransRoutesService.getAll(dateStr).then(data => {
			const routes = data?.data || [];
			const inboundRoutes = routes.filter(route => route.type === 'inbound');
			const solidSeatIds = new Set();
			const notAbsentIds = new Set();
			const solidSeatPersonalStatuses = [PERSONAL_ROUTE_STATUS.PICKED, PERSONAL_ROUTE_STATUS.IN_CENTER];
			const absentPersonalStatuses = [
				PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT,
				PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT,
				PERSONAL_ROUTE_STATUS.DISABLED,
			];
			inboundRoutes.forEach(route => {
				route.route_customer_list?.forEach(customer => {
					const customerId = `${customer.customer_id || ''}`.trim();
					if (!customerId) return;
					if (solidSeatPersonalStatuses.includes(customer.customer_route_status)) {
						solidSeatIds.add(customerId);
					}
					if (!absentPersonalStatuses.includes(customer.customer_route_status)) {
						notAbsentIds.add(customerId);
					}
				});
			});
			setInCenterCustomerIds([...solidSeatIds]);
			setNotAbsentCustomerIds([...notAbsentIds]);
		});
	}, [selectedDate]);

	const manualRefreshSeatingPage = useCallback(() => {
		fetchSeatingData();
		fetchMealRecords();
		setAutoRefreshScheduleToken((t) => t + 1);
	}, [fetchSeatingData, fetchMealRecords]);

	// Initial load: customers + labels (only once)
	useEffect(() => {
		CustomerService.getAllActiveCustomers().then(data => setCustomers(data?.data));
		LabelService.getAll('active').then((data) => {setCurrentLabels(data?.data); setOriginalLabelsList(data?.data)});
	}, []);

	// Fetch date-dependent data when selectedDate changes
	useEffect(() => {
		fetchSeatingData();
		fetchMealRecords();
	}, [fetchSeatingData, fetchMealRecords]);

	/** Auto-refresh seating + routes + meal records every 60s; interval resets when date changes or user refreshes manually. */
	useEffect(() => {
		const id = window.setInterval(() => {
			fetchSeatingData();
			fetchMealRecords();
		}, SEATING_PAGE_AUTO_REFRESH_MS);
		return () => window.clearInterval(id);
	}, [fetchSeatingData, fetchMealRecords, autoRefreshScheduleToken]);

	// OLD ROW-BASED LOGIC - COMMENTED OUT
	// useEffect(() => {
	// 	const newRows = Array.from({length: rowCount}, (_, index) => ({
	// 		id: index+1,
	// 		tables: tableCountsPerRow[index] || 0
	// 	}));
	// 	if (rowCount !== tableLayout.rows?.length) {
	// 		setTableLayout(prevLayout => ({
	// 			...prevLayout,
	// 			rows: newRows
	// 		}));
	// 		if (tableCountsPerRow.length !== rowCount) {
	// 			setTableCountsPerRow(prev => {
	// 				const newCounts = [...prev];
	// 				while(newCounts.length < newCounts) {
	// 					newCounts.push(0);
	// 				}
	// 				return newCounts.slice(0, rowCount);
	// 			})
	// 		}
	// 	}
	// }, [rowCount]);

	// NEW COLUMN-BASED LOGIC
	useEffect(() => {
		const newColumns = Array.from({length: columnCount}, (_, index) => ({
			id: index+1,
			tables: tableCountsPerColumn[index] || 0
		}));
		if (columnCount !== tableLayout.columns?.length) {
			setTableLayout(prevLayout => ({
				...prevLayout,
				columns: newColumns
			}));
			if (tableCountsPerColumn.length !== columnCount) {
				setTableCountsPerColumn(prev => {
					const newCounts = [...prev];
					while(newCounts.length < columnCount) {
						newCounts.push(0);
					}
					return newCounts.slice(0, columnCount);
				})
			}
		}
	}, [columnCount]);

	// OLD ROW-BASED LOGIC - COMMENTED OUT
	// useEffect(() => {
	// 	const newTables = {};
	// 	let tableNumber = 1;
	// 	let startUpdate = false;

	// 	if (tableCountsPerRow.length !== tableLayout.rows.length) {
	// 		startUpdate = true;
	// 	} else {
	// 		for (let i = 0; i<tableLayout?.rows.length; i++) {
	// 			if (tableLayout.rows[i].tables !== tableCountsPerRow[i]) {
	// 				startUpdate = true;
	// 				break;
	// 			}
	// 		}
	// 	}

	// 	if (startUpdate) {
	// 		tableLayout.rows.forEach((row, rowIndex) => {
	// 			const tableCount = tableCountsPerRow[rowIndex] || 0;
	// 			row.tables = tableCount;
	// 			for (let tableIndex = 0; tableIndex < tableCount; tableIndex++) {
	// 				const tableId = `table-${tableNumber}`
	// 				if (!newTables[tableId]) {
	// 					const seats = Array.from({length: 8}, (_, seatIndex) => {
	// 						const seatLetter = String.fromCharCode(65 + seatIndex);
	// 						return {
	// 							id: `${tableNumber}-${seatLetter}`,
	// 							name: seatLetter,
	// 							customerName: '',
	// 							customerId: '',
	// 							rowIndex: rowIndex + 1,
	// 							label: {label_name: undefined, label_color: undefined}
	// 						}
	// 					});
	// 					newTables[tableId] = {
	// 						id: tableNumber,
	// 						tableId: tableId,
	// 						seats,
	// 						expanded: false,
	// 						rowIndex: rowIndex + 1
	// 					}
	// 				} else {
	// 					newTables[tableId] = tableLayout.tables[tableId];
	// 				}
	// 				tableNumber++;
	// 			}
	// 		})
	// 		setTableLayout(prevLayout => ({
	// 			...prevLayout,
	// 			rows: prevLayout.rows.map((item, index) => ({...item, tables: tableCountsPerRow[index]}) ),
	// 			tables: newTables
	// 		}));
	// 	}
	// }, [tableCountsPerRow, tableLayout.rows]);

	// NEW COLUMN-BASED LOGIC — preserve seat assignments by grid slot (column-major); new slots start empty.
	useEffect(() => {
		setTableLayout((prevLayout) => {
			if (!columnCount || columnCount < 1) return prevLayout;

			let startUpdate = false;
			if (tableCountsPerColumn.length !== columnCount) {
				startUpdate = true;
			} else if ((prevLayout.columns?.length || 0) !== columnCount) {
				startUpdate = true;
			} else {
				for (let i = 0; i < columnCount; i++) {
					const prevT = prevLayout.columns?.[i]?.tables;
					const nextT = tableCountsPerColumn[i];
					if (prevT !== nextT) {
						startUpdate = true;
						break;
					}
				}
			}

			if (!startUpdate) return prevLayout;

			const prevCounts = (prevLayout.columns || []).map((col) => col.tables || 0);
			const oldFlat = buildSeatingSlotOrderFromCounts(prevCounts);
			const oldTables = prevLayout.tables || {};

			const newTables = {};
			let tableNumber = 1;
			let slotIndex = 0;

			for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
				const tableCount = tableCountsPerColumn[columnIndex] || 0;
				for (let tableIndex = 0; tableIndex < tableCount; tableIndex++) {
					const tableId = `table-${tableNumber}`;
					const prevKey = slotIndex < oldFlat.length ? oldFlat[slotIndex] : null;
					const prevTable = prevKey ? oldTables[prevKey] : null;

					if (prevTable && Array.isArray(prevTable.seats)) {
						newTables[tableId] = {
							...prevTable,
							tableId,
							columnIndex: columnIndex + 1,
							expanded: prevTable.expanded ?? false,
							seats: prevTable.seats.map((seat) => ({
								...seat,
								columnIndex: columnIndex + 1,
								id: `${tableNumber}${seat.name}`,
							})),
						};
					} else {
						const seats = Array.from({ length: 8 }, (_, seatIndex) => {
							const seatLetter = String.fromCharCode(65 + seatIndex);
							return {
								id: `${tableNumber}${seatLetter}`,
								name: seatLetter,
								customerName: '',
								customerId: '',
								columnIndex: columnIndex + 1,
								label: { label_name: undefined, label_color: undefined },
							};
						});
						newTables[tableId] = {
							id: tableNumber,
							tableId,
							seats,
							expanded: false,
							columnIndex: columnIndex + 1,
						};
					}
					tableNumber++;
					slotIndex++;
				}
			}

			return {
				...prevLayout,
				columns: Array.from({ length: columnCount }, (_, index) => ({
					id: prevLayout.columns?.[index]?.id ?? index + 1,
					tables: tableCountsPerColumn[index] ?? 0,
				})),
				tables: newTables,
			};
		});
	}, [tableCountsPerColumn, columnCount, tableLayout.columns]);

	// OLD ROW-BASED LOGIC - COMMENTED OUT
	// const handleRowCountChange = (e) => {
	// 	const count = parseInt(e.target.value) || 0;
	// 	setRowCount(count);
	// }

	// const handleTableCountChange = (rowIndex, count) => {
	// 	setTableCountsPerRow(prev => {
	// 		const newCounts = [...prev];
	// 		newCounts[rowIndex] = parseInt(count) || 0;
	// 		return newCounts;
	// 	})
	// }

	// NEW COLUMN-BASED LOGIC
	const handleColumnCountChange = (e) => {
		const count = parseInt(e.target.value) || 0;
		setColumnCount(count);
	}

	const handleTableCountChange = (columnIndex, count) => {
		setTableCountsPerColumn(prev => {
			const newCounts = [...prev];
			newCounts[columnIndex] = parseInt(count) || 0;
			return newCounts;
		})
	}

	const toggleTableExpansion = (tableId) => {
		setTableLayout(prevLayout => ({
			...prevLayout,
			tables: {
				...prevLayout.tables,
				[tableId]: {
					...prevLayout.tables[tableId],
					expanded: !prevLayout.tables[tableId].expanded
				}
			}
		}));
	}

	const startEditingTableNumber = (tableId) => {
		setEditingTableId(tableId);
		setEditingTableNumber(`${tableLayout?.tables?.[tableId]?.id ?? ''}`);
	};

	const cancelEditingTableNumber = () => {
		setEditingTableId(null);
		setEditingTableNumber('');
	};

	const saveEditingTableNumber = (tableId) => {
		const nextValue = parseInt(`${editingTableNumber}`.trim(), 10);
		if (!Number.isFinite(nextValue) || nextValue <= 0) {
			window.alert('Table number must be a positive number.');
			return;
		}
		const duplicated = Object.entries(tableLayout?.tables || {}).some(([currentTableId, table]) => (
			currentTableId !== tableId && parseInt(`${table?.id || ''}`, 10) === nextValue
		));
		if (duplicated) {
			window.alert(`Table number ${nextValue} already exists. Please use a unique number.`);
			return;
		}
		const nextLayout = {
			...tableLayout,
			tables: {
				...tableLayout.tables,
				[tableId]: {
					...tableLayout.tables[tableId],
					id: nextValue
				}
			}
		};
		const editorName = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name;
		const now = new Date();
		if (currentDateRecord?.id) {
			SeatingService.updateSeating(currentDateRecord.id, {
				seating_assignment: nextLayout,
				update_by: editorName,
				update_date: now
			}).catch((err) => {
				console.error('Failed to save seating table number', err);
				window.alert('Failed to save table number. Please try again.');
			});
		} else {
			const dateStr = moment(selectedDate).format('MM/DD/YYYY');
			SeatingService.createNewSeating({
				date: dateStr,
				seating_assignment: nextLayout,
				create_by: editorName,
				update_by: editorName,
				create_date: now,
				update_date: now
			})
				.then((res) => {
					if (res?.data) setCurrentDateRecord(res.data);
				})
				.catch((err) => {
					console.error('Failed to create seating record for table number', err);
					window.alert('Failed to save table number. Please try again.');
				});
		}
		setTableLayout(nextLayout);
		cancelEditingTableNumber();
	};

	const cleanAndClose = () => {
		setTableLayout(currentDateRecord?.seating_assignment || initialValue);
		// OLD ROW-BASED LOGIC - COMMENTED OUT
		// setRowCount(currentDateRecord?.seating_assignment?.rows?.length || 1);
		// setTableCountsPerRow(currentDateRecord?.seating_assignment?.rows?.map(item => item.tables));
		
		// NEW COLUMN-BASED LOGIC
		setColumnCount(currentDateRecord?.seating_assignment?.columns?.length || 1);
		setTableCountsPerColumn(currentDateRecord?.seating_assignment?.columns?.map(item => item.tables) || [1]);
		setShowTableLayoutConfig(false);
		setSelectedCustomer(undefined);
		setSelectedCustomerLabel(undefined);
	}

	const saveAndClose = () => {
		if (currentDateRecord) {
			SeatingService.updateSeating(currentDateRecord?.id, { seating_assignment: tableLayout, update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, update_date: new Date() })
		} else {
			SeatingService.createNewSeating({
				date: moment(selectedDate).format('MM/DD/YYYY'),
				seating_assignment: tableLayout,
				create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
				update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
				create_date: new Date(),
				update_date: new Date()
			}).then((data) => {
				const result = data?.data;
				setCurrentDateRecord(result);
			})
		}

		setShowTableLayoutConfig(false);
		setSelectedCustomer(undefined);
		setSelectedCustomerLabel(undefined);
		
	}

	const [keyword, setKeyword] = useState('');

	// OLD ROW-BASED LOGIC - COMMENTED OUT
	// const getTotalTableNumber = () => tableCountsPerRow.reduce((acc, curr) => acc + curr, 0);

	// const tablesByRow = () => tableLayout.rows.map((row, rowIndex) => {
	// 	const rowTables = Object.values(tableLayout.tables).filter(table => table.rowIndex === rowIndex + 1).sort((a,b) => a.id - b.id);
	// 	return {
	// 		rowId: row.id,
	// 		tables: rowTables
	// 	}
	// })

	// NEW COLUMN-BASED LOGIC
	const getTotalTableNumber = () => tableCountsPerColumn.reduce((acc, curr) => acc + curr, 0);

	const sortTablesBySlotOrder = (tables) =>
		[...tables].sort((a, b) => {
			const aSeq = parseInt(`${a?.tableId || ''}`.replace('table-', ''), 10) || 0;
			const bSeq = parseInt(`${b?.tableId || ''}`.replace('table-', ''), 10) || 0;
			return aSeq - bSeq;
		});

	// One stack per data column: tables read top-to-bottom, columns laid out left-to-right (列居中)
	const tablesByVisualColumn = () => {
		const visualColumns = [];
		tableLayout.columns?.forEach((column, columnIndex) => {
			const columnTables = sortTablesBySlotOrder(
				Object.values(tableLayout.tables || {}).filter(
					(table) => table.columnIndex === columnIndex + 1
				)
			);
			if (columnTables.length > 0) {
				visualColumns.push({
					columnId: columnIndex + 1,
					tables: columnTables
				});
			}
		});
		return visualColumns;
	};

	// Get all customer IDs that are already assigned to seats
	const getAssignedCustomerIds = () => {
		const assignedIds = new Set();
		Object.values(tableLayout.tables || {}).forEach(table => {
			table.seats?.forEach(seat => {
				getSeatAssignedCustomers(seat).forEach((assigned) => {
					if (assigned.customerId) assignedIds.add(`${assigned.customerId}`);
				});
			});
		});
		return assignedIds;
	};

	const assignmentsFromSelectValue = (selected) => (
		(Array.isArray(selected) ? selected : [])
			.filter((item) => `${item?.value || ''}`.trim())
			.map((item) => ({
				customerId: item.value,
				customerName: item.label,
			}))
	);

	const seatAssignmentSummaryNames = (seat) => {
		const assigned = getSeatAssignedCustomers(seat);
		if (!assigned.length) return '';
		return assigned
			.map((c) => seatingDisplayNameByCustomerId[c.customerId] || c.customerName || '')
			.filter(Boolean)
			.join(', ');
	};

	const startEditingSeat = (tableId, seatId) => {
		const seat = tableLayout.tables[tableId].seats.find(seat => seat.id === seatId);
		const assigned = getSeatAssignedCustomers(seat);
		const selectedOptions = assigned.map((c) => ({ value: c.customerId, label: c.customerName }));
		setEditingSeat({
			tableId,
			seatId,
			customerId: assigned[0]?.customerId || '',
			label: seat?.label,
			customerName: assigned[0]?.customerName || '',
			customers: assigned,
		});
		setSelectedCustomer(selectedOptions);
		setSelectedCustomerLabel({value: seat?.label?.id, label: seat?.label?.label_name});
	};

	const onCustomerChange = (selectedCustomers) => {
		const nextCustomers = assignmentsFromSelectValue(selectedCustomers);
		setEditingSeat({
			...editingSeat,
			customers: nextCustomers,
			customerId: nextCustomers[0]?.customerId || '',
			customerName: nextCustomers[0]?.customerName || '',
		});
		setSelectedCustomer(Array.isArray(selectedCustomers) ? selectedCustomers : []);
	}

	const handleLabelChange = (selectedLabel) => {
		const nextLabel = selectedLabel || { value: '', label: '' };
		setEditingSeat({
			...editingSeat,
			label: {
				label_name: nextLabel.label,
				id: nextLabel.value,
				label_color: currentLabels.find(item => item.id === nextLabel.value)?.label_color
			}
		})
		setSelectedCustomerLabel(nextLabel);
	}

	const handleNewLabelNameChange = (e) => {
		setNewLabel(prevLabel => ({
			...prevLabel,
			label_name: e.target.value
		}))
	}

	const handleNewLabelColorChange = (selectedColor) => {
		setSelectedLabelColor(selectedColor);
		setNewLabel(prevLabel => ({
			...prevLabel,
			label_color: selectedColor.value
		}))
	}

	const saveSeatEdit = () =>{
		if (!editingSeat) return;

		const {tableId, seatId, label} = editingSeat;
		const nextCustomers = getSeatAssignedCustomers({
			customers: editingSeat.customers,
			customerId: editingSeat.customerId,
			customerName: editingSeat.customerName,
		});
		const displayed = pickDisplayedSeatCustomer(
			{ customers: nextCustomers },
			notAbsentCustomerIds
		);
		const customerId = displayed?.customerId || nextCustomers[0]?.customerId || '';
		const customerName = displayed?.customerName || nextCustomers[0]?.customerName || '';
		const editMeta = {
			edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
			edit_date: new Date(),
		};

		setTableLayout(prevLayout => {
			const previousSeat = prevLayout.tables[tableId]?.seats?.find((seat) => seat.id === seatId);
			const previousIds = new Set(getSeatAssignedCustomers(previousSeat).map((c) => c.customerId));
			const nextIds = new Set(nextCustomers.map((c) => c.customerId));

			const updatedSeats = prevLayout.tables[tableId].seats.map(seat => seat.id === seatId ? {
				...seat,
				customerName,
				customerId,
				customers: nextCustomers,
				label,
			} : seat);
			const finalResult = {
				...prevLayout,
				tables: {
					...prevLayout.tables,
					[tableId]: {
						...prevLayout.tables[tableId],
						seats: updatedSeats
					}
				}
			};
			SeatingService.updateSeating(currentDateRecord?.id, { seating_assignment: finalResult, update_by: editMeta.edit_by, update_date: editMeta.edit_date});

			previousIds.forEach((previousCustomerId) => {
				if (previousCustomerId && !nextIds.has(previousCustomerId)) {
					CustomerService.updateCustomer(previousCustomerId, {
						table_id: '',
						seating: '',
						seat_number: '',
						...editMeta,
					});
				}
			});

			nextCustomers.forEach((assigned) => {
				const nextCustomerId = assigned.customerId;
				if (!nextCustomerId) return;
				const targetCustomer = customers.find(item => item.id === nextCustomerId);
				const existingTags = Array.isArray(targetCustomer?.tags) ? targetCustomer.tags : [];
				const nextTags = label?.label_name
					? Array.from(new Set([...existingTags, label.label_name]))
					: existingTags;
				const tableRecord = prevLayout.tables[tableId];
				const chartTableNumber =
					tableRecord?.id != null && `${tableRecord.id}`.trim() !== ''
						? String(tableRecord.id)
						: tableId;
				const chartSeatLabel =
					formatSeatDisplayLikeSeatingChart(chartTableNumber, seatId) || seatId;
				CustomerService.updateCustomer(nextCustomerId, {
					table_id: chartTableNumber,
					seating: seatId,
					seat_number: chartSeatLabel,
					tags: nextTags,
					...editMeta,
				});
			});
			return finalResult;
		});
		
		setEditingSeat(null);
		setSelectedCustomer([]);
	}

	const cancelSeatEdit = () => {
		setEditingSeat(null);
		setSelectedCustomer([]);
	}

	const saveLabelAndClose = () => {
		if (newLabel.label_color && newLabel.label_name) {
			LabelService.createNewLabel(newLabel).then(() => {
				LabelService.getAll('active').then(data => {
					setCurrentLabels(data?.data);
					setOriginalLabelsList(data?.data);
					setShowLabelConfig(false);
					setStartAddLabel(false);
					setNewLabel({ label_color: undefined, label_name: undefined})
				});
			});
		} 
		if (deletedItems.length > 0) {
			Promise.all(deletedItems.map(item => LabelService.updateLabel(item.id, {status: 'inactive'}))).then(() => setDeletedItems([]));
		}
		
	}

	const cleanLabelAndClose = () => {
		setCurrentLabels(originalLabelsList);
		setShowLabelConfig(false);
		setStartAddLabel(false);
		setEditingLabelId(null);
		setEditingLabelName('');
		setEditingLabelColorId(null);
	}

	const onDeleteLabel = (item) => {
		setCurrentLabels(prevLabels => prevLabels.filter(lb => lb.id !== item.id))
		setDeletedItems(prevLabels => ([
			...prevLabels,
			item
		]))
	}

	const startEditingLabel = (item) => {
		setEditingLabelId(item.id);
		setEditingLabelName(item.label_name);
	}

	const saveEditingLabel = () => {
		if (!editingLabelId || !editingLabelName.trim()) return;
		LabelService.updateLabel(editingLabelId, { label_name: editingLabelName.trim() }).then(() => {
			LabelService.getAll('active').then(data => {
				setCurrentLabels(data?.data);
				setOriginalLabelsList(data?.data);
			});
		});
		setEditingLabelId(null);
		setEditingLabelName('');
	}

	const cancelEditingLabel = () => {
		setEditingLabelId(null);
		setEditingLabelName('');
	}

	const saveEditingLabelColor = (labelId, newColor) => {
		LabelService.updateLabel(labelId, { label_color: newColor }).then(() => {
			LabelService.getAll('active').then(data => {
				setCurrentLabels(data?.data);
				setOriginalLabelsList(data?.data);
			});
		});
		setEditingLabelColorId(null);
	}

	const handleFullScreen = () => {
		setIsFullScreen(true);
	}

	const handleExitFullScreen = () => {
		setIsFullScreen(false);
	}

	// Meal confirmation functions
	const handleSeatClick = (guest) => {
		if (guest?.customerId && guest?.customerName) {
			setSelectedSeatCustomer(guest);
			setShowMealModal(true);
		}
	};

	const closeMealModal = () => {
		setShowMealModal(false);
		setSelectedSeatCustomer(null);
	};

	const hasBreakfast = (customerId) => {
		return breakfastRecords.some(record => 
			record.customer_id === customerId && 
			record.date === moment(selectedDate).format('MM/DD/YYYY')
		);
	};

	const hasLunch = (customerId) => {
		return lunchRecords.some(record => 
			record.customer_id === customerId && 
			record.date === moment(selectedDate).format('MM/DD/YYYY')
		);
	};

	const hasSnack = (customerId) => {
		return snackRecords.some(record => 
			record.customer_id === customerId && 
			record.date === moment(selectedDate).format('MM/DD/YYYY')
		);
	};

	const confirmBreakfast = (customer) => {
		TransRoutesService.createBreakfastRecords({
			customer_id: customer?.customerId,
			customer_name: customer?.customerName,
			has_breakfast: true,
			date: moment(selectedDate).format('MM/DD/YYYY'),
			create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
			create_date: new Date(),
			edit_history: [{
				employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
				date: new Date()
			}]
		}).then(() => {
			fetchMealRecords();
		});
	};

	const confirmLunch = (customer) => {
		TransRoutesService.createLunchRecords({
			customer_id: customer?.customerId,
			customer_name: customer?.customerName,
			has_lunch: true,
			date: moment(selectedDate).format('MM/DD/YYYY'),
			create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
			create_date: new Date(),
			edit_history: [{
				employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
				date: new Date()
			}]
		}).then(() => {
			fetchMealRecords();
		});
	};

	const confirmSnack = (customer) => {
		TransRoutesService.createSnackRecords({
			customer_id: customer?.customerId,
			customer_name: customer?.customerName,
			has_snack: true,
			date: moment(selectedDate).format('MM/DD/YYYY'),
			create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
			create_date: new Date(),
			edit_history: [{
				employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
				date: new Date()
			}]
		}).then(() => {
			fetchMealRecords();
		});
	};

	const removeBreakfast = (customerId) => {
		const record = breakfastRecords.find(b => 
			b.customer_id === customerId && 
			b.date === moment(selectedDate).format('MM/DD/YYYY')
		);
		if (record) {
			TransRoutesService.deleteBreakfastRecords(record.id).then(() => {
				fetchMealRecords();
			});
		}
	};

	const removeLunch = (customerId) => {
		const record = lunchRecords.find(b => 
			b.customer_id === customerId && 
			b.date === moment(selectedDate).format('MM/DD/YYYY')
		);
		if (record) {
			TransRoutesService.deleteLunchRecords(record.id).then(() => {
				fetchMealRecords();
			});
		}
	};

	const removeSnack = (customerId) => {
		const record = snackRecords.find(b => 
			b.customer_id === customerId && 
			b.date === moment(selectedDate).format('MM/DD/YYYY')
		);
		if (record) {
			TransRoutesService.deleteSnackRecords(record.id).then(() => {
				fetchMealRecords();
			});
		}
	};

	const handleBreakfastChange = (e) => {
		if (e.target.checked) {
			confirmBreakfast(selectedSeatCustomer);
		} else {
			removeBreakfast(selectedSeatCustomer?.customerId);
		}
	};

	const handleLunchChange = (e) => {
		if (e.target.checked) {
			confirmLunch(selectedSeatCustomer);
		} else {
			removeLunch(selectedSeatCustomer?.customerId);
		}
	};

	const handleSnackChange = (e) => {
		if (e.target.checked) {
			confirmSnack(selectedSeatCustomer);
		} else {
			removeSnack(selectedSeatCustomer?.customerId);
		}
	};

	// Handle ESC key to exit full screen
	useEffect(() => {
		const handleKeyPress = (event) => {
			if (event.key === 'Escape' && isFullScreen) {
				handleExitFullScreen();
			}
		};

		if (isFullScreen) {
			document.addEventListener('keydown', handleKeyPress);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyPress);
		};
	}, [isFullScreen]);

	return (
		<>
			{/* Full Screen Mode */}
			{isFullScreen && (
				<div className="app-main-content-list-container fullscreen-mode seating-fullscreen" style={{ backgroundColor: '#fff' }}>
					<div className="seating-chart-container">
						<div className="seating-stage">
							<div className="stage">Stage</div>
						</div>
						<div
							ref={fullscreenTablesRowRef}
							className="seating-tables-by-column"
							style={
								fullscreenInterColumnGapPx == null
									? undefined
									: { gap: `0 ${fullscreenInterColumnGapPx}px` }
							}
						>
							{tablesByVisualColumn().map((colItem, colIndex) => (
								<div className="seating-column" id={`seating-col-fullscreen-${colIndex}`} key={`seating-col-fullscreen-${colItem.columnId}`}>
									{colItem?.tables?.map((item, itemIndex) => (
										<CircularTable isFullScreen={true} key={`seating-table-fullscreen-${colItem.columnId}-${itemIndex}`} id={`seating-table-fullscreen-${colItem.columnId}-${itemIndex}`} tableNumber={item?.id} guests={item?.seats} inCenterCustomerIds={inCenterCustomerIds} notAbsentCustomerIds={notAbsentCustomerIds} onSeatClick={handleSeatClick} labels={currentLabels} customerDisplayNames={seatingDisplayNameByCustomerId} />
									))}
								</div>
							))}
						</div>
						<div className="seating-stage mt-4">
							{
								currentLabels.map((item) => <div key={item.id}><span style={{width: '16px', height: '16px', borderRadius: '16px', display: 'inline-block', background: item.label_color, marginRight: '8px' }}></span>{item.label_name}</div>)
							}
						</div>
					</div>
					<div className="fullscreen-hint">
						Press ESC to exit full screen
					</div>
				</div>
			)}

			{/* Normal Mode - Original Layout */}
			{!isFullScreen && (
				<div className="seating-page-container">
					<div className="list row mb-4">
						<Breadcrumb>
							<Breadcrumb.Item href="/">Lobby</Breadcrumb.Item>
							<Breadcrumb.Item active>
								Seating Chart
							</Breadcrumb.Item>
						</Breadcrumb>
						<div className="col-md-12 text-primary">
							<h4>Seating Chart</h4>
						</div>
						<div className="col-md-12 d-flex align-items-center" style={{ marginTop: '8px', marginBottom: '8px' }}>
							<div className="d-flex align-items-center" style={{ gap: '12px' }}>
								<button className="btn btn-outline-secondary btn-sm" onClick={goToPreviousDay} title="Previous day">
									<span style={{ fontSize: '18px', lineHeight: 1 }}>&lsaquo;</span>
								</button>
								<span style={{ fontWeight: 600, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>
									{moment(selectedDate).format('ddd, MMM D, YYYY')}
								</span>
								<button className="btn btn-outline-secondary btn-sm" onClick={goToNextDay} title="Next day" disabled={isToday}>
									<span style={{ fontSize: '18px', lineHeight: 1 }}>&rsaquo;</span>
								</button>
								{!isToday && (
									<button className="btn btn-outline-primary btn-sm" onClick={goToToday}>
										Today
									</button>
								)}
								<button
									type="button"
									className="btn btn-outline-secondary btn-sm"
									onClick={manualRefreshSeatingPage}
									title="Refresh data (resets 60s auto-refresh timer)"
								>
									<ArrowClockwise size={16} className="me-1" aria-hidden />
									Refresh
								</button>
								<button
									type="button"
									className="btn btn-primary me-2"
									onClick={exportSeatingChartDietary}
								>
									<Download size={16} className="me-2" />
									Export
								</button>
							</div>
						</div>
						<div className="app-main-content-list-container">
							<div className="app-main-content-list-func-container">
								<Tabs defaultActiveKey="seatingChart" id="seatingTabs">
									<Tab eventKey="seatingChart" title="Seating Chart">
										<div className="seating-chart-container">
											<div className="seating-stage">
												<div className="stage">Stage</div>
											</div>
											<div className="seating-tables-by-column">
												{tablesByVisualColumn().map((colItem, colIndex) => (
													<div className="seating-column" id={`seating-col-${colIndex}`} key={`seating-col-${colItem.columnId}`}>
														{colItem?.tables?.map((item, itemIndex) => (
															<CircularTable key={`seating-table-${colItem.columnId}-${itemIndex}`} id={`seating-table-${colItem.columnId}-${itemIndex}`} tableNumber={item?.id} guests={item?.seats} inCenterCustomerIds={inCenterCustomerIds} notAbsentCustomerIds={notAbsentCustomerIds} onSeatClick={handleSeatClick} labels={currentLabels} customerDisplayNames={seatingDisplayNameByCustomerId} />
														))}
													</div>
												))}
											</div>
											<div className="seating-stage mt-4">
												{
													currentLabels.map((item) => <div key={item.id}><span style={{width: '16px', height: '16px', borderRadius: '16px', display: 'inline-block', background: item.label_color, marginRight: '8px' }}></span>{item.label_name}</div>)
												}
											</div>
										</div>
									</Tab>
								</Tabs>
								<div className="list-func-panel">
									<button className="btn btn-primary me-2" onClick={handleFullScreen}>
										<ArrowsFullscreen size={16} className="me-2"></ArrowsFullscreen>Full Screen
									</button>
								</div>
							</div>
						</div>
					</div>
					{canManageSeatingChart && (
					<div
						className={
							'manage-seating-chart-container' +
							(manageSeatingPanelCollapsed ? ' manage-seating-chart-container--collapsed' : '')
						}
					>
				<div className="manage-seating-chart-title-container">
					{!manageSeatingPanelCollapsed && <h6 className="mb-0">Manage Seating Chart</h6>}
					<button
						type="button"
						className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center p-1"
						onClick={() => setManageSeatingPanelCollapsed((c) => !c)}
						title={manageSeatingPanelCollapsed ? 'Show Manage Seating Chart' : 'Hide panel (show more chart)'}
						aria-expanded={!manageSeatingPanelCollapsed}
						aria-label={manageSeatingPanelCollapsed ? 'Expand Manage Seating Chart' : 'Collapse Manage Seating Chart to the right'}
					>
						{manageSeatingPanelCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
					</button>
				</div>
				{!manageSeatingPanelCollapsed && (
				<div className="manage-seating-chart-tables-container">
					<div className="title">
						<strong>Table Layout</strong>
						<PencilSquare color="#777" size={16} onClick={() => { setShowLabelConfig(false); setShowTableLayoutConfig(true); }}/>
						{
							showTableLayoutConfig && <div className="seating-popover">
								<h6>Manage Table Layout</h6>
								{/* OLD ROW-BASED LOGIC - COMMENTED OUT */}
								{/* <div className="app-main-content-fields-section margin-sm  dropdown-container">
									<div className="me-4">
										<div className="field-label">Number of Rows</div>
										<input type="number" min="0" value={rowCount} onChange={handleRowCountChange}></input>
									</div>
								</div>
								{
									rowCount>0 && Array.from({length: rowCount}).map((_, rowIndex) => (
										<div className="app-main-content-fields-section margin-sm  dropdown-container">
											<div className="me-4">
												<div className="field-label">{`Row ${rowIndex + 1}`}</div>
												<input type="number" min="0" value={tableCountsPerRow[rowIndex] || 0} onChange={(e) => handleTableCountChange(rowIndex, e.target.value)}/>
											</div>
										</div>
									))
								} */}

								{/* NEW COLUMN-BASED LOGIC */}
								<div className="app-main-content-fields-section margin-sm  dropdown-container">
									<div className="me-4">
										<div className="field-label">Number of Columns</div>
										<input type="number" min="0" value={columnCount} onChange={handleColumnCountChange}></input>
									</div>
								</div>
								{
									columnCount > 0 && Array.from({length: columnCount}).map((_, columnIndex) => (
										<div className="app-main-content-fields-section margin-sm  dropdown-container" key={`column-config-${columnIndex}`}>
											<div className="me-4">
												<div className="field-label">{`Column ${columnIndex + 1} (Tables)`}</div>
												<input type="number" min="0" value={tableCountsPerColumn[columnIndex] || 0} onChange={(e) => handleTableCountChange(columnIndex, e.target.value)}/>
											</div>
										</div>
									))
								}
								
								<div className="list row">
									<div className="col-md-12">
										<button className="btn btn-default btn-sm float-right" onClick={() => cleanAndClose()}> Cancel </button>
										<button className="btn btn-primary btn-sm float-right" onClick={() => saveAndClose()}> Save </button>
									</div>
								</div>
							</div>
						}
					</div>
					{
						Object.keys(tableLayout.tables).map((tableId) => (
							<div className="table-config-container" key={`table-config-${tableId}`}>
								<div className="table-config-item-title">
									{!tableLayout.tables[tableId]?.expanded && <ChevronDown className="me-2" color="#777" size={12} onClick={() => toggleTableExpansion(tableId)} />}
									{tableLayout.tables[tableId]?.expanded && <ChevronRight className="me-2" color="#777" size={12} onClick={() => toggleTableExpansion(tableId)} />}
									{editingTableId === tableId ? (
										<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<span style={{ fontSize: '12px', color: '#666' }}>Table</span>
											<input
												type="number"
												min="1"
												value={editingTableNumber}
												onChange={(e) => setEditingTableNumber(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') saveEditingTableNumber(tableId);
													if (e.key === 'Escape') cancelEditingTableNumber();
												}}
												style={{ width: '74px', height: '28px', fontSize: '12px' }}
											/>
											<button className="btn btn-primary btn-sm" style={{ padding: '1px 8px', fontSize: '12px' }} onClick={() => saveEditingTableNumber(tableId)}>OK</button>
											<button className="btn btn-default btn-sm" style={{ padding: '1px 8px', fontSize: '12px' }} onClick={cancelEditingTableNumber}>X</button>
										</div>
									) : (
										<>
											<span>{`Table ${tableLayout.tables[tableId]?.id ?? ''}`}</span>
											<PencilSquare className="ms-2" color="#777" size={14} onClick={() => startEditingTableNumber(tableId)} />
										</>
									)}
								</div>
								{tableLayout.tables[tableId]?.expanded && tableLayout.tables[tableId]?.seats.map(seat => <div className="table-config-item" key={`${tableId}-${seat.id}`}>
									{`${seat.name}. ${seatAssignmentSummaryNames(seat)}`} <PencilSquare color="#777" size={16} onClick={() => startEditingSeat(tableId, seat.id)} />
									{
										editingSeat && editingSeat.tableId === tableId && editingSeat.seatId === seat.id && <div className="seating-popover">
											<h6>Seat Assignment</h6>
											<div className="app-main-content-fields-section margin-sm  dropdown-container">
												<div className="me-4">
													<div className="field-label">Customer Name</div>
													<Select
														isMulti
														isClearable
														closeMenuOnSelect={false}
														placeholder="Select customers"
														styles={{
														control: (baseStyles) => ({
															...baseStyles,
															width: '260px',
															minHeight: '43px',
															borderRadius: '8px'
														}),
														indicatorSeparator: () => ({
															display: 'none'
														}),
														dropdownIndicator: (baseStyles) => ({
															...baseStyles,
															paddingRight: '8px',
															color: '#333',
															'&:hover': { color: '#000' }
														}),
														valueContainer: (baseStyles) => ({
															...baseStyles,
															minHeight: '43px',
															padding: '4px 8px',
														}),
														input: (baseStyles) => ({
															...baseStyles,
															margin: '0px',
															padding: '0px',
														}),
														placeholder: (baseStyles) => ({
															...baseStyles,
															fontSize: '13px'
														}),
														multiValue: (baseStyles) => ({
															...baseStyles,
															fontSize: '12px'
														}),
														multiValueLabel: (baseStyles) => ({
															...baseStyles,
															fontSize: '12px'
														})
													}} value={selectedCustomer} onChange={newSelectedCustomer => onCustomerChange(newSelectedCustomer)} options={customers.filter(customer => {
														const assignedIds = getAssignedCustomerIds();
														const selectedOnThisSeat = new Set(
															(Array.isArray(selectedCustomer) ? selectedCustomer : [])
																.map((item) => item?.value)
																.filter(Boolean)
														);
														return isCustomerListActive(customer) &&
															(!assignedIds.has(`${customer?.id || ''}`) || selectedOnThisSeat.has(customer?.id));
													}).map(customer => ({
														value: customer?.id || '',
														label: customer?.name || ''
													}))}></Select>
													{Array.isArray(selectedCustomer) && selectedCustomer.filter((item) => item?.value).length > 1 && (
														<div className="seating-seat-absent-note">
															{(() => {
																const assigned = assignmentsFromSelectValue(selectedCustomer);
																const present = assigned.filter((c) => notAbsentCustomerIds.includes(c.customerId));
																const presentNames = present.map((c) => seatingDisplayNameByCustomerId[c.customerId] || c.customerName);
																if (present.length === 0) {
																	return 'All selected customers are marked absent on today\'s inbound routes.';
																}
																if (present.length === 1) {
																	return `Not absent today: ${presentNames[0]}. Shown on the seating chart.`;
																}
																return `Not absent today: ${presentNames.join(', ')}. The seating chart shows ${presentNames[0]}.`;
															})()}
														</div>
													)}
												</div>
											</div>
											<div className="app-main-content-fields-section margin-sm  dropdown-container">
												<div className="me-4">
													<div className="field-label">Customer Label</div>
													<Select styles={{
														control: (baseStyles) => ({
															...baseStyles,
															width: '210px',
															borderRadius: '8px'
														}),
														indicatorSeparator: () => ({
															display: 'none'
														}),
														dropdownIndicator: (baseStyles) => ({
															...baseStyles,
															paddingRight: '8px',
															color: '#333',
															'&:hover': { color: '#000' }
														}),
														valueContainer: (baseStyles) => ({
															...baseStyles,
															height: '43px',
															padding: '0 8px',
														}),
														input: (baseStyles) => ({
															...baseStyles,
															margin: '0px',
															padding: '0px',
															height: '30px',
															width: '150px'
														}),
														placeholder: (baseStyles) => ({
															...baseStyles,
															fontSize: '13px'
														}),
														singleValue: (baseStyles) => ({
															...baseStyles,
															margin: '0px',
															fontSize: '13px'
														})
													}} value={ selectedCustomerLabel} onChange={selectedLabel => handleLabelChange(selectedLabel)} options={[{value: '', label: ''}, ...currentLabels.map(label => ({
														value: label?.id|| '',
														label: label?.label_name || ''
													}))]}></Select>
												</div>
											</div>
											
											<div className="list row">
												<div className="col-md-12 d-flex">
													<button className="btn btn-primary btn-sm me-2" onClick={() => saveSeatEdit()}> Save </button>
													<button className="btn btn-default btn-sm" onClick={() => cancelSeatEdit()}> Cancel </button>
												</div>
											</div>
										</div>
									}
								</div>)}
							</div>
						))
					}
					<div className="title">
						<strong>Customer Label</strong>
						<PencilSquare color="#777" size={16} onClick={() => { setShowTableLayoutConfig(false); setShowLabelConfig(true); }}/>
						{
							showLabelConfig && <div className="seating-popover">
								<h6>Customer Labels</h6>
								<div className="mb-4">
									{
										currentLabels.map((item) => <div className="label-delete-item" key={item.id}>
											<span 
												style={{width: '16px', height: '16px', borderRadius: '16px', background: item.label_color, flexShrink: 0, cursor: 'pointer', border: editingLabelColorId === item.id ? '2px solid #333' : 'none' }}
												onClick={() => setEditingLabelColorId(editingLabelColorId === item.id ? null : item.id)}
												title="Click to change color"
											></span>
											{editingLabelId === item.id ? (
												<div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, marginLeft: '8px' }}>
													<input
														type="text"
														value={editingLabelName}
														onChange={(e) => setEditingLabelName(e.target.value)}
														onKeyDown={(e) => { if (e.key === 'Enter') saveEditingLabel(); if (e.key === 'Escape') cancelEditingLabel(); }}
														style={{ flex: 1, fontSize: '13px', padding: '2px 6px', border: '1px solid #aaa', borderRadius: '4px' }}
														autoFocus
													/>
													<button className="btn btn-primary btn-sm" style={{ padding: '1px 8px', fontSize: '12px' }} onClick={saveEditingLabel}>OK</button>
													<button className="btn btn-default btn-sm" style={{ padding: '1px 8px', fontSize: '12px' }} onClick={cancelEditingLabel}>X</button>
												</div>
											) : (
												<>
													<span style={{ flex: 1, marginLeft: '8px' }}>{item.label_name}</span>
													<PencilSquare size={14} color="#777" style={{ cursor: 'pointer', marginRight: '8px' }} onClick={() => startEditingLabel(item)} />
													<FileX size={16} style={{ cursor: 'pointer' }} onClick={() => onDeleteLabel(item)} />
												</>
											)}
											{editingLabelColorId === item.id && (
												<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', padding: '6px', background: '#f8f8f8', borderRadius: '6px', width: '100%' }}>
													{colorsList.map(color => (
														<span
															key={color}
															onClick={() => saveEditingLabelColor(item.id, color)}
															style={{
																width: '20px', height: '20px', borderRadius: '50%', background: color, cursor: 'pointer',
																border: item.label_color === color ? '2px solid #333' : '2px solid transparent',
																display: 'inline-block'
															}}
														></span>
													))}
												</div>
											)}
										</div>)
									}
								</div>
								{!startAddLabel && <button className="btn btn-tertiary btn-custom-label btn-sm mb-4" onClick={() => setStartAddLabel(true)}>+ Add New Label</button>}
								{startAddLabel && <>
									<div className="app-main-content-fields-section margin-sm  dropdown-container">
										<div className="me-4">
											<div className="field-label">Label Name</div>
											<input type="text" value={newLabel.label_name} onChange={handleNewLabelNameChange}></input>
										</div>
									</div>
									<div className="app-main-content-fields-section margin-sm  dropdown-container">
										<div className="me-4">
											<div className="field-label">Label Color</div>
											{/* <select value={newLabel.label_color} onChange={handleNewLabelColorChange}>
												<option value=""></option>
												{
													colorsList.map((item) => <option style={{backgroundColor: item}} value={item}><div style={{backgroundColor: item, width: '4px', height: '4px'}}></div>{item}</option>)
												}
											</select> */}
											<Select styles={{
												control: (baseStyles) => ({
													...baseStyles,
													width: '210px',
													borderRadius: '8px'
												}),
												indicatorSeparator: () => ({
													display: 'none'
												}),
												dropdownIndicator: (baseStyles) => ({
													...baseStyles,
													paddingRight: '8px',
													color: '#333',
													'&:hover': { color: '#000' }
												}),
												valueContainer: (baseStyles) => ({
													...baseStyles,
													height: '43px',
													padding: '0 8px',
												}),
												input: (baseStyles) => ({
													...baseStyles,
													margin: '0px',
													padding: '0px',
													height: '30px',
													width: '150px'
												}),
												placeholder: (baseStyles) => ({
													...baseStyles,
													fontSize: '13px'
												}),
												singleValue: (baseStyles) => ({
													...baseStyles,
													margin: '0px',
													fontSize: '13px'
												}),
												option: (baseStyles, {data}) => ({
													...baseStyles,
													backgroundColor: data?.value
												})
											}} value={selectedLabelColor} onChange={selectedColor => handleNewLabelColorChange(selectedColor)} options={[{value: '', label: ''}, ...colorsList.map(color => ({
												value: color|| '',
												label: color || ''
											}))]}></Select>
										</div>
									</div>
								</>}
								
								<div className="list row">
									<div className="col-md-12">
										<button className="btn btn-default btn-sm float-right" onClick={() => cleanLabelAndClose()}> Cancel </button>
										<button className="btn btn-primary btn-sm float-right" onClick={() => saveLabelAndClose()}> Save </button>
									</div>
								</div>
							</div>
						}
					</div>
					{
					currentLabels.map((item) => <div className="mb-4" style={{fontSize: '12px', display: 'inline-flex', alignItems: 'center', marginRight: '16px'}} key={item.id}><span style={{width: '16px', height: '16px', borderRadius: '16px', background: item.label_color, display: 'inline-block', marginRight: '8px' }}></span>{item.label_name}</div>)
				}
				</div>
				)}
			</div>
			)}
				</div>
			)}

			{/* Meal Confirmation Modal */}
			<Modal show={showMealModal} onHide={closeMealModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Confirm Meals</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedSeatCustomer && (
						<>
							<p className="mb-3">
								<strong>Customer:</strong> {selectedSeatCustomer.customerName}
							</p>
							<p className="text-muted mb-3">Please confirm the meals for today:</p>
							<Form>
								<Form.Check 
									type="checkbox"
									id="breakfast-check"
									label="Breakfast"
									checked={hasBreakfast(selectedSeatCustomer?.customerId)}
									onChange={handleBreakfastChange}
									className="mb-2"
								/>
								<Form.Check 
									type="checkbox"
									id="lunch-check"
									label="Lunch"
									checked={hasLunch(selectedSeatCustomer?.customerId)}
									onChange={handleLunchChange}
									className="mb-2"
								/>
								<Form.Check 
									type="checkbox"
									id="snack-check"
									label="Snack"
									checked={hasSnack(selectedSeatCustomer?.customerId)}
									onChange={handleSnackChange}
									className="mb-2"
								/>
							</Form>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={closeMealModal}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Seating;